import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Manages per-device search history and trending data in localStorage.
 * No API keys or third-party services required.
 */
class SearchHistory {
  static #HISTORY_KEY = '__srch_h__';
  static #TRENDING_KEY = '__srch_t__';
  static #MAX_HISTORY = 8;
  static #MAX_TRENDING_STORED = 40;

  static #read(key) {
    try {
      return JSON.parse(localStorage.getItem(key) ?? 'null');
    } catch {
      return null;
    }
  }

  static #write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // localStorage unavailable (private mode, storage full, etc.)
    }
  }

  static getHistory() {
    return this.#read(this.#HISTORY_KEY) ?? [];
  }

  /** Add a term to recent searches and increment its trending score. */
  static add(term) {
    const normalized = term.trim().toLowerCase();
    if (!normalized || normalized.length < 2) return;

    const history = this.getHistory().filter((t) => t !== normalized);
    history.unshift(normalized);
    this.#write(this.#HISTORY_KEY, history.slice(0, this.#MAX_HISTORY));
    this.#incrementTrending(normalized);
  }

  static remove(term) {
    this.#write(
      this.#HISTORY_KEY,
      this.getHistory().filter((t) => t !== term)
    );
  }

  static clearHistory() {
    try {
      localStorage.removeItem(this.#HISTORY_KEY);
    } catch {}
  }

  /** Returns most-searched terms that are NOT already in recent history. */
  static getTrending(limit = 5) {
    const data = this.#read(this.#TRENDING_KEY);
    if (!data) return [];
    const history = new Set(this.getHistory());
    return Object.entries(data)
      .sort((a, b) => b[1] - a[1])
      .map(([term]) => term)
      .filter((term) => !history.has(term))
      .slice(0, limit);
  }

  static #incrementTrending(term) {
    const data = this.#read(this.#TRENDING_KEY) ?? {};
    data[term] = (data[term] ?? 0) + 1;
    // Keep only the top N entries to prevent unbounded storage growth
    const trimmed = Object.fromEntries(
      Object.entries(data)
        .sort((a, b) => b[1] - a[1])
        .slice(0, this.#MAX_TRENDING_STORED)
    );
    this.#write(this.#TRENDING_KEY, trimmed);
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    // Amazon-style Voice Search Logic
    const voiceBtn = this.querySelector('#voice-search-btn');
    if (voiceBtn) {
      if ('webkitSpeechRecognition' in window) {
        const recognition = new window.webkitSpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;

        voiceBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          recognition.start();
          voiceBtn.style.opacity = '0.5';
        });

        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          if (this.refs.searchInput) {
            this.refs.searchInput.value = transcript;
            this.search({ inputType: 'insertText' });
          }
          voiceBtn.style.opacity = '1';
        };

        recognition.onend = () => {
          voiceBtn.style.opacity = '1';
        };
      } else {
        voiceBtn.style.display = 'none';
      }
    }

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const term = this.refs.searchInput.value.trim();
          if (term) SearchHistory.add(term);
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', term);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Builds the history + trending HTML element. Returns null when there's nothing to show.
   * @returns {HTMLElement | null}
   */
  #buildHistoryUI() {
    const history = SearchHistory.getHistory();
    const trending = SearchHistory.getTrending(5);

    if (history.length === 0 && trending.length === 0) return null;

    const wrap = document.createElement('div');
    wrap.className = 'search-history-trending';

    if (history.length > 0) {
      const section = document.createElement('div');
      section.className = 'search-history-section predictive-search-results__products';
      section.innerHTML = `
        <h4 class="predictive-search-results__title">
          Recent searches
          <button class="predictive-search-results__clear button button-unstyled" type="button" data-action="clear-history">
            Clear all
          </button>
        </h4>
        <ul class="predictive-search-results__list predictive-search-results__wrapper-queries list-unstyled">
          ${history
            .map(
              (term) => `
            <li class="predictive-search-results__card--query search-history-item" data-term="${this.#esc(term)}" ref="resultsItems[]">
              <div class="search-history-row">
                <a class="pills__pill predictive-search-results__pill search-history-pill" href="${Theme.routes.search_url}?q=${encodeURIComponent(term)}&options[prefix]=last" data-search-term="${this.#esc(term)}">
                  <svg class="search-history-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <span>${this.#esc(term)}</span>
                </a>
                <button class="search-history-remove button-unstyled" type="button" data-remove-term="${this.#esc(term)}" aria-label="Remove ${this.#esc(term)} from history">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </li>`
            )
            .join('')}
        </ul>`;
      wrap.appendChild(section);
    }

    if (trending.length > 0) {
      const section = document.createElement('div');
      section.className = 'search-trending-section predictive-search-results__products';
      section.innerHTML = `
        <h4 class="predictive-search-results__title">Trending searches</h4>
        <ul class="predictive-search-results__list predictive-search-results__wrapper-queries list-unstyled">
          ${trending
            .map(
              (term) => `
            <li class="predictive-search-results__card--query" ref="resultsItems[]">
              <a class="pills__pill predictive-search-results__pill search-trending-pill" href="${Theme.routes.search_url}?q=${encodeURIComponent(term)}&options[prefix]=last" data-search-term="${this.#esc(term)}">
                <svg class="search-trending-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                <span>${this.#esc(term)}</span>
              </a>
            </li>`
            )
            .join('')}
        </ul>`;
      wrap.appendChild(section);
    }

    return wrap;
  }

  /** Minimal HTML escape to prevent XSS when injecting dynamic content. */
  #esc(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Injects the history/trending UI into the current results container.
   * Must be called AFTER a morph so it doesn't get wiped immediately.
   */
  #renderHistoryUI() {
    const inner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
    if (!inner) return;

    const historyEl = this.#buildHistoryUI();
    if (!historyEl) return;

    inner.insertAdjacentElement('afterbegin', historyEl);

    // Clear all history
    historyEl.querySelector('[data-action="clear-history"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      SearchHistory.clearHistory();
      historyEl.querySelector('.search-history-section')?.remove();
      // If only trending remains, keep it; otherwise remove the whole block
      if (!historyEl.querySelector('.search-history-section, .search-trending-section')) {
        historyEl.remove();
      }
    });

    // Remove individual history term
    historyEl.querySelectorAll('[data-remove-term]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const term = /** @type {HTMLElement} */ (btn).dataset.removeTerm ?? '';
        SearchHistory.remove(term);
        btn.closest('[data-term]')?.remove();
        // Remove the section header if the list is now empty
        const ul = historyEl.querySelector('.search-history-section ul');
        if (ul && ul.children.length === 0) {
          historyEl.querySelector('.search-history-section')?.remove();
        }
        if (!historyEl.querySelector('.search-history-section, .search-trending-section')) {
          historyEl.remove();
        }
      });
    });

    // Fill input and trigger live search when a history/trending pill is clicked
    historyEl.querySelectorAll('[data-search-term]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const term = /** @type {HTMLElement} */ (link).dataset.searchTerm ?? '';
        SearchHistory.add(term);
        this.refs.searchInput.value = term;
        this.#showResetButton();
        this.#getSearchResults(term);
        this.refs.searchInput.focus();
      });
    });
  }

  /**
   * Attach one-time click listeners to result links so we record which search
   * term the user actually followed through on.
   * @param {string} searchTerm
   */
  #trackResultClicks(searchTerm) {
    this.refs.predictiveSearchResults.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener(
        'click',
        () => {
          SearchHistory.add(searchTerm);
        },
        { once: true }
      );
    });
  }

  /**
   * Builds a predictive-search URL for a given query term.
   * @param {string} term
   * @returns {URL}
   */
  #buildSearchUrl(term) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '8');
    return url;
  }

  /**
   * Detects compound words typed without a space (e.g. "watergun") and returns
   * the most natural space-expanded form ("water gun") using VCCV syllable
   * boundary detection. Returns null when no confident split is found.
   *
   * How it works: in English, the pattern Vowel-Consonant-Consonant-Vowel (VCCV)
   * almost always indicates a word boundary between the two consonants.
   * "wa-t-e-r | g-u-n" → "e r|g u" matches VCCV → split after 'r' → "water gun".
   *
   * @param {string} term - raw query, may be multi-word
   * @returns {string | null}
   */
  #expandCompound(term) {
    // Only act on single words that are long enough to plausibly be compound
    if (term.includes(' ') || term.length < 5) return null;

    const vowels = 'aeiou';
    const w = term.toLowerCase();
    const isV = (ch) => !!ch && vowels.includes(ch);
    const isC = (ch) => !!ch && !vowels.includes(ch);

    const candidates = [];

    for (let i = 1; i < w.length - 2; i++) {
      // VCCV: vowel before, consonant at i, consonant at i+1, vowel at i+2
      if (isV(w[i - 1]) && isC(w[i]) && isC(w[i + 1]) && isV(w[i + 2])) {
        const pos = i + 1; // split between the two consonants (after w[i])
        if (pos >= 2 && w.length - pos >= 2) {
          candidates.push({ pos, dist: Math.abs(pos - w.length / 2) });
        }
      }
    }

    if (!candidates.length) return null;

    // Prefer the split closest to the middle of the word
    candidates.sort((a, b) => a.dist - b.dist);
    const { pos } = candidates[0];

    return term.slice(0, pos) + ' ' + term.slice(pos);
  }

  /**
   * Counts product card occurrences in an HTML string without DOM parsing.
   * Used to decide which of two result sets is more relevant.
   * @param {string} markup
   * @returns {number}
   */
  #countProductCards(markup) {
    if (!markup) return 0;
    const marker = 'predictive-search-results__card--product';
    let count = 0;
    let pos = 0;
    while ((pos = markup.indexOf(marker, pos)) !== -1) {
      count++;
      pos += marker.length;
    }
    return count;
  }

  /**
   * Merges two HTML result strings into one by taking the larger set as the
   * base and injecting any unique products from the smaller set.
   *
   * "watergun" → 1 product + "water gun" → 2 products = merged 3 products.
   * "water gun" → 2 products + "watergun" → 1 product = merged 3 products.
   *
   * @param {string} markupA - HTML from one query
   * @param {string} markupB - HTML from the alternate query
   * @returns {string} merged HTML
   */
  #mergeSearchResults(markupA, markupB) {
    const countA = this.#countProductCards(markupA);
    const countB = this.#countProductCards(markupB);

    // Always use the result set with more products as the DOM base
    const [baseMarkup, addMarkup] = countA >= countB ? [markupA, markupB] : [markupB, markupA];

    const parser = new DOMParser();
    const baseDoc = parser.parseFromString(baseMarkup, 'text/html');
    const addDoc = parser.parseFromString(addMarkup, 'text/html');

    const baseList = baseDoc.querySelector('.predictive-search-results__wrapper-products');
    const addCards = Array.from(addDoc.querySelectorAll('.predictive-search-results__card--product'));

    if (!baseList || !addCards.length) return baseMarkup;

    // Track product URLs already in the base to avoid duplicates
    const seen = new Set(
      Array.from(baseList.querySelectorAll('a[href]'))
        .map((a) => a.getAttribute('href'))
        .filter(Boolean)
    );

    let added = 0;
    for (const card of addCards) {
      const href = card.querySelector('a[href]')?.getAttribute('href') ?? '';
      if (href && !seen.has(href)) {
        baseList.appendChild(baseDoc.importNode(card, true));
        seen.add(href);
        added++;
      }
    }

    if (!added) return baseMarkup;

    const root =
      baseDoc.querySelector('.predictive-search-dropdown') ??
      baseDoc.querySelector('#predictive-search-results');
    return root ? root.outerHTML : baseMarkup;
  }

  /**
   * Returns an alternate form of the query to run in parallel and merge:
   * - Single word   → space-expand using VCCV  ("watergun"  → "water gun")
   * - 2–3 word phrase → collapse spaces        ("water gun" → "watergun")
   * Returns null when no useful alternate can be derived.
   * @param {string} term
   * @returns {string | null}
   */
  #alternateTerm(term) {
    if (!term.includes(' ')) {
      // Single word: try VCCV compound expansion
      return this.#expandCompound(term);
    }

    // Multi-word: collapse spaces to catch compound-word product titles
    const words = term.trim().split(/\s+/);
    if (words.length > 3) return null; // too many words; collapsing won't help
    const collapsed = words.join('');
    return collapsed.length >= 4 && collapsed.length <= 16 ? collapsed : null;
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * For every query, also fires a parallel alternate fetch (compound-expanded or
   * space-collapsed) and MERGES both result sets — so typing "watergun" or
   * "water gun" always returns the full combined product list.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { predictiveSearchResults } = this.refs;
    this.classList.add('is-loading');
    const abortController = this.#createAbortController();

    const primaryUrl = this.#buildSearchUrl(searchTerm);
    const altTerm = this.#alternateTerm(searchTerm);
    const altUrl = altTerm ? this.#buildSearchUrl(altTerm) : null;

    try {
      // Both fetches fire simultaneously — no added latency
      const [primaryMarkup, altMarkup] = await Promise.all([
        sectionRenderer.getSectionHTML(this.dataset.sectionId, false, primaryUrl),
        altUrl
          ? sectionRenderer.getSectionHTML(this.dataset.sectionId, false, altUrl)
          : Promise.resolve(null),
      ]);

      if (abortController.signal.aborted) return;
      if (!primaryMarkup) return;

      const bestMarkup = altMarkup
        ? this.#mergeSearchResults(primaryMarkup, altMarkup)
        : primaryMarkup;

      morph(predictiveSearchResults, bestMarkup);
      this.classList.remove('is-loading');
      this.#resetScrollPositions();
      this.#trackResultClicks(searchTerm);
    } catch (error) {
      this.classList.remove('is-loading');
      if (abortController.signal.aborted) return;
      throw error;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.classList.remove('is-loading');

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();

    // Inject history/trending AFTER morph so it isn't overwritten
    this.#renderHistoryUI();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
